import type { HitArea } from '../../elements/HitArea';
import type {
  Anchor,
  NonZeroVector,
  Point,
  Vector,
} from '../../elements/index';
import type { ISize } from '../../elements/types';
import type { NodeStyle } from '../NodeStyle';

export type NodeIconOptions = {
  scale?: number;
  style?: NodeStyle;
  /** additional xml attributes of the root svg tag */
  attributes?: Record<string, string | number>;
};

export const nodeIconDefaultOptions = {
  scale: 1,
  style: {
    background: 'blue',
    outline: undefined, // helps properly infer the type
  },
} satisfies Partial<NodeIconOptions>;

export interface NodeLabelStyle {
  /**
   * Relative position of "anchor" point (see below) of the label to node center
   */
  offset: Vector;
  anchor: Anchor;
}

export const nodeIconShapes = ['rect', 'circle', 'cloud'] as const;

export type NodeIconShape = (typeof nodeIconShapes)[number];

/**
 * Generic configuration to be provided by every icon type.
 *
 * Designed to be minimal in configuration, positioning methods
 * like getClippingPoint assume center at [0, 0] and no scaling.
 *
 * For functions depending on NodeStyle, NodeStyle | undefined
 * is intentionally used over optional argument to be forced
 * to always pass relevant node style. It is because NodeData.nodeStyle
 * is optional, but forgetting to pass it here when it is present
 * would result in irrelevant results.
 */
export interface NodeTypeIcon {
  createIconSvg: (options?: NodeIconOptions) => string;
  /**
   * Return clipping point for the icon's default size, assuming icon's center at [0, 0].
   *
   * If you are using a scaled version, you have to scale this point too.
   */
  makeGetClippingPoint: (
    nodeStyle: NodeStyle | undefined,
  ) => (direction: NonZeroVector) => Point;
  getHitArea: (nodeStyle: NodeStyle | undefined) => HitArea;
  getLabelStyle: (nodeStyle: NodeStyle | undefined) => NodeLabelStyle;
  /** Maximum distance between icon center and edge */
  getMaxRadius: (nodeStyle: NodeStyle | undefined) => number;
  getSize: (nodeStyle: NodeStyle | undefined) => ISize;
  /** Node icon shape - generic circle, square, or special shapes of cloud and transit */
  shape: 'circle' | 'rectangle' | 'cloud' | 'transit';
}
